/**
 * Route guards.
 *
 * These decide what to render, not what a user may do. The API is the authorisation
 * boundary and enforces its own rules on every route; removing every guard here would
 * change what the interface *shows*, not what it can *reach*. That separation is
 * deliberate, and it is why a guard is allowed to be optimistic where the API is not.
 *
 * The distinction these guards are careful about: a route the user cannot use still
 * exists. Opening it renders a no-access state, not a not-found. One tells the user their
 * access is wrong, the other tells them the address is wrong, and only one of those is
 * true.
 */

import { ButtonField, Icon2, Typography } from '@wk/components-v3-react16'
import { Suspense, useState, type ReactNode } from 'react'
import {
  Navigate,
  isRouteErrorResponse,
  useLocation,
  useRouteError,
} from 'react-router'

import { runtimeConfig } from '../config/runtime'
import type { ModuleRegistry } from '../modules/registry'
import { hasRole, useAuth } from '../auth/AuthProvider'
import { ROLES, ROLE_LABELS, type Role } from '../auth/identity'
import { TextInput } from '../ui/fields'
import { ErrorState, LoadingState, NoAccessState } from '../ui/states'

/**
 * Which synthetic worker each role starts as.
 *
 * The identifiers are real people in the fixture organisation, chosen so the first screen
 * each role lands on actually has data on it. The fixture builds two directors
 * (W100000–W100001), four managers (W100002–W100005), and individual contributors from
 * W100006, so a manager's scope is a genuine subtree rather than one person.
 *
 * Getting this wrong is not cosmetic: signing in as a developer whose identifier matches
 * nobody lands on "No AI tools are assigned to you", which is a second dead end
 * immediately after the first.
 */
const STARTING_WORKER: Readonly<Record<Role, string>> = {
  developer: 'W100006',
  manager: 'W100002',
  'governance-admin': 'W100000',
  'platform-admin': 'W100000',
  executive: 'W100000',
}

/**
 * The local sign-in.
 *
 * There is no dialogue to show, because there is no identity provider — so this is where
 * the artificial identity gets created, and it asks which role to create it for.
 *
 * It exists because the alternative did not work. The screen used to offer one "Sign in"
 * button that called the authenticator's `signIn`, which returns whatever identity has
 * already been adopted — and locally, nothing ever has. The only thing that adopts one is
 * the role switcher in the application shell, and the shell only renders once this guard
 * has let something through. So the button did nothing, for ever, and the application was
 * unreachable on a fresh start.
 *
 * Asking for the role here is also better than the button was. The identity is declared at
 * the moment it is invented, rather than appearing by default and being corrected
 * afterwards in a bar most people would not read.
 */
function LocalSignIn({
  assumeRole,
}: {
  readonly assumeRole: (roles: readonly Role[], workerId?: string) => void
}) {
  const [role, setRole] = useState<Role>('developer')
  const [workerId, setWorkerId] = useState(STARTING_WORKER.developer)
  // Tracks whether the field has been edited, so switching role still updates the default
  // worker but never overwrites something typed deliberately.
  const [workerEdited, setWorkerEdited] = useState(false)

  const choose = (next: Role): void => {
    setRole(next)
    if (!workerEdited) setWorkerId(STARTING_WORKER[next])
  }

  return (
    <div
      className="devex-boot devex-local-signin"
      data-testid="sign-in-required"
    >
      <div className="devex-local-signin__banner">
        <Icon2 name="wk-icon-caution" flowClass="devex-icon--small" />
        <Typography>
          <span>Local development only &mdash; this is not a real sign-in</span>
        </Typography>
      </div>

      <Typography>
        <h1>Choose who to be</h1>
      </Typography>
      <Typography>
        <p>
          There is no identity provider running locally, so pick a role and this
          will adopt it. You can change it at any time from the bar across the
          top.
        </p>
      </Typography>

      <fieldset className="devex-local-signin__roles">
        <legend>Role</legend>
        {ROLES.map((candidate) => (
          <label key={candidate} className="devex-local-signin__role">
            <input
              type="radio"
              name="devex-role"
              value={candidate}
              checked={role === candidate}
              onChange={() => choose(candidate)}
            />
            <span>{ROLE_LABELS[candidate]}</span>
          </label>
        ))}
      </fieldset>

      <TextInput
        label="Worker"
        value={workerId}
        description="Whose record you are. The default is somebody the local fixture data knows about."
        onChange={(next) => {
          setWorkerEdited(true)
          setWorkerId(next)
        }}
      />

      <ButtonField type="primary">
        <button
          type="button"
          onClick={() =>
            assumeRole(
              [role],
              workerId.trim() === '' ? undefined : workerId.trim(),
            )
          }
        >
          Continue as {ROLE_LABELS[role]}
        </button>
      </ButtonField>
    </div>
  )
}

/** Waits for the identity to resolve, then offers sign-in if there is none. */
export function RequireAuth({ children }: { readonly children: ReactNode }) {
  const { status, identity, error, signIn, assumeRole } = useAuth()

  if (status === 'initialising') {
    return (
      <div className="devex-boot">
        <LoadingState label="Checking who you are" />
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="devex-boot">
        <ErrorState
          error={new Error(error ?? 'Sign-in failed.')}
          onRetry={() => void signIn()}
        />
      </div>
    )
  }

  if (identity === undefined) {
    // `assumeRole` is defined only for the development authenticator, so its presence is
    // what distinguishes "there is no identity provider here" from "you have not signed in
    // to the real one yet".
    if (assumeRole !== undefined) {
      return <LocalSignIn assumeRole={assumeRole} />
    }

    return (
      <div className="devex-boot" data-testid="sign-in-required">
        <Typography>
          <h1>Sign in to continue</h1>
        </Typography>
        <Typography>
          <p>
            This application shows consumption and governance data for named
            employees, so it requires a sign-in.
          </p>
        </Typography>
        <ButtonField type="primary">
          <button type="button" onClick={() => void signIn()}>
            Sign in
          </button>
        </ButtonField>
      </div>
    )
  }

  return <>{children}</>
}

export function RequireRole({
  roles,
  children,
}: {
  readonly roles: readonly Role[]
  readonly children: ReactNode
}) {
  const { identity } = useAuth()
  if (!hasRole(identity, ...roles)) {
    return <NoAccessState />
  }
  return <>{children}</>
}

/**
 * Sends a freshly signed-in user to the first area their roles permit.
 *
 * Derived from the registry rather than hard-coded, so a developer lands on their own
 * usage and a manager on the overview without either being special-cased — and a role with
 * no areas at all gets an explanation instead of a redirect loop.
 */
export function LandingRedirect({
  registry,
}: {
  readonly registry: ModuleRegistry
}) {
  const { identity } = useAuth()
  const config = runtimeConfig()
  const target = registry.landingPath(config, identity?.roles ?? [])

  if (target === undefined) {
    return (
      <NoAccessState
        title="There is nothing here for your role yet"
        description="Your sign-in worked, but no area of this application is available to your current role. Ask your manager or the governance team to review it."
      />
    )
  }
  return <Navigate to={target} replace />
}

/**
 * An address that matches no route.
 *
 * Separate from the error boundary because it is not an error: nothing failed, the address
 * simply does not exist. Routing it through the error boundary would have shown the user a
 * retry button for a request that was never made.
 */
export function NotFoundState() {
  const location = useLocation()
  return (
    <div className="devex-route-error" data-testid="not-found-state">
      <Typography>
        <h1>That address does not exist</h1>
      </Typography>
      <Typography>
        <p>
          Nothing in this application is served at{' '}
          <code>{location.pathname}</code>. The link may be out of date. Use the
          navigation on the left to find what you were looking for.
        </p>
      </Typography>
    </div>
  )
}

/**
 * The route-level error boundary.
 *
 * Catches a render failure in any screen so the shell survives it. A crash that takes the
 * navigation down with it leaves the user with no way out but the browser's back button.
 */
export function RouteErrorBoundary() {
  const error = useRouteError()

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundState />
  }

  return (
    <div className="devex-route-error">
      <ErrorState error={error} onRetry={() => window.location.reload()} />
    </div>
  )
}

/**
 * The boundary around a lazily-loaded screen.
 *
 * Each screen is its own chunk, so this is also what the user sees while that chunk
 * downloads on a slow connection.
 */
export function ScreenSuspense({ children }: { readonly children: ReactNode }) {
  return (
    <Suspense fallback={<LoadingState label="Loading this screen" />}>
      {children}
    </Suspense>
  )
}
